import type { ReactNode } from "react";
import strings from "global/constants/StringConstants";
import { store } from "utils/store";
import viewpaths from "global/constants/ViewPathConstants";
import FeatureKeys from "global/constants/FeatureKeys";
import DashboardIcon from "../assets/icons/SideNavIcon/bulkSendIcon.svg";
import TemplatesIcon from "../assets/icons/SideNavIcon/templateIcon.svg";
import ContactsIcon from "../assets/icons/SideNavIcon/contactsIcon.svg";
import InboxIcon from "../assets/icons/SideNavIcon/inboxIcon.svg";
import SentIcon from "../assets/icons/SideNavIcon/sentIcon.svg";
import SettingsIcon from "../assets/icons/SideNavIcon/requestSettingIcon.svg";
import { SvgIcon } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  getCompareIcon,
  getContactsIcon,
  getHomeIcon,
  getContractSearchIcon,
  getInboxIcon,
  getInsightsIcon,
  getReportsIcon,
  getSentIcon,
  getSettingsIcon,
  getTemplatesIcon,
  getWebFormsIcon,
} from "utils/DrawerIcons";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  accessTo: string[];
  accessToResource: string[];
  underDevelopment?: boolean;
  visibleInSideNav?: boolean;
  featureKey?: string;
  beta?: boolean;
  /** Short label for a chip beside the title (e.g. "NEW"); `beta` stays the "Beta" pill. */
  badge?: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const useSections = (): Section[] => {
  return [
    {
      subheader: "",
      items: [
        {
          title: strings.DASHBOARD,
          icon: <SvgIcon>{getHomeIcon("var(--nav-item-icon-color)")}</SvgIcon>,
          activeIcon: (
            <SvgIcon>{getHomeIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
          ),
          path: "/dashboard",
          accessToResource: [strings.resources.sign],
          accessTo: ["all"],
          visibleInSideNav: true,
        },
        {
          // Intelligent Contract Repository. Same read permission as the sign
          // screens; deliberately no featureKey (not plan-gated, 2026-09-16).
          title: strings.CONTRACT_SEARCH,
          icon: (
            <SvgIcon>
              {getContractSearchIcon("var(--nav-item-icon-color)")}
            </SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>
              {getContractSearchIcon("var(--nav-item-icon-active-color)")}
            </SvgIcon>
          ),
          path: viewpaths.contractSearchViewPath,
          accessToResource: [strings.resources.sign],
          accessTo: ["all"],
          visibleInSideNav: true,
        },
        {
          // Gated by AI Assistant (shows the lock when the plan lacks it); Contract Search above stays ungated.
          title: strings.contractCompare.page.navItem,
          icon: <SvgIcon>{getCompareIcon("var(--nav-item-icon-color)")}</SvgIcon>,
          activeIcon: (
            <SvgIcon>
              {getCompareIcon("var(--nav-item-icon-active-color)")}
            </SvgIcon>
          ),
          path: viewpaths.contractCompareViewPath,
          accessToResource: [strings.resources.sign],
          accessTo: ["all"],
          visibleInSideNav: true,
          featureKey: FeatureKeys.AI_ASSISTANT,
          badge: strings.contractCompare.page.navNewChip,
        },
        {
          title: strings.TEMPLATES,
          icon: (
            <SvgIcon>{getTemplatesIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>
              {getTemplatesIcon("var(--nav-item-icon-active-color)")}
            </SvgIcon>
          ),
          path: "/templates",
          accessToResource: [strings.resources.template],
          accessTo: ["all"],
          visibleInSideNav: true,
          featureKey: FeatureKeys.TEMPLATE_USE,
        },
        {
          title: strings.WEB_FORMS,
          icon: (
            <SvgIcon>{getWebFormsIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>{getWebFormsIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
          ),
          path: "/webforms",
          accessToResource: [strings.resources.webforms],
          accessTo: ["all"],
          visibleInSideNav: true,
          featureKey: FeatureKeys.ENVELOPE_WEB_FORMS,
          beta: true,
        },
        {
          title: strings.CONTACTS,
          icon: (
            <SvgIcon>{getContactsIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>{getContactsIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
          ),
          path: "/contacts",
          accessToResource: [strings.resources.contacts],
          accessTo: ["all"],
          visibleInSideNav: true,
        },

        // {
        //   title: strings.INBOX,
        //   icon: <SvgIcon>{getInboxIcon("var(--nav-item-icon-color)")}</SvgIcon>,
        //   activeIcon: (
        //     <SvgIcon>{getInboxIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
        //   ),
        //   path: "/requests/inbox",
        //   accessToResource: [strings.resources.sign],
        //   accessTo: ["all"],
        //   visibleInSideNav: true,
        // },
        {
          title: strings.INBOX,
          icon: <SvgIcon>{getInboxIcon("var(--nav-item-icon-color)")}</SvgIcon>,
          activeIcon: (
            <SvgIcon>{getInboxIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
          ),
          path: "/requests/inbox",
          accessToResource: [strings.resources.sign],
          accessTo: ["all"],
          visibleInSideNav: false,
        },
        {
          title: strings.SIGNREQUEST,
          icon: <SvgIcon>{getSentIcon("var(--nav-item-icon-color)")}</SvgIcon>,
          activeIcon: (
            <SvgIcon>{getSentIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
          ),
          path: "/requests/sent",
          visibleInSideNav: true,
          accessToResource: [strings.resources.sign],
          accessTo: ["all"],
        },
        // ...(isAdmin()
        //   ? [
        //     {
        //       title: strings.CONFIGURATIONS,
        //       icon: (
        //         <SvgIcon>
        //           {getSettingsIcon("var(--nav-item-icon-color)")}
        //         </SvgIcon>
        //       ),
        //       activeIcon: (
        //         <SvgIcon>
        //           {getSettingsIcon("var(--nav-item-icon-active-color)")}
        //         </SvgIcon>
        //       ),
        //       path: "/requests/settings",
        //       visibleInSideNav: true,
        //       accessToResource: [strings.resources.sign],
        //       accessTo: ["all"],
        //     },
        //   ]
        //   : []),
        {
          title: "Reports",
          icon: (
            <SvgIcon>{getReportsIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>
              {getReportsIcon("var(--nav-item-icon-active-color)")}
            </SvgIcon>
          ),
          accessToResource: [strings.resources.sign],
          accessTo: ["all"],
          visibleInSideNav: true,
          featureKey: FeatureKeys.REPORTING_ANALYTICS,
          items: [
            {
              title: strings.INSIGHTS,
              icon: (
                <SvgIcon>{getInsightsIcon("var(--nav-item-icon-color)")}</SvgIcon>
              ),
              activeIcon: (
                <SvgIcon>{getInsightsIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
              ),
              path: viewpaths.insightsViewPath,
              accessToResource: [strings.resources.sign],
              accessTo: ["all"],
              visibleInSideNav: true,
              featureKey: FeatureKeys.REPORTING_ANALYTICS,
            },
            {
              title: strings.ANALYTICS,
              icon: (
                <SvgIcon>{getReportsIcon("var(--nav-item-icon-color)")}</SvgIcon>
              ),
              activeIcon: (
                <SvgIcon>{getReportsIcon("var(--nav-item-icon-active-color)")}</SvgIcon>
              ),
              path: viewpaths.reports,
              visibleInSideNav: true,
              accessToResource: [strings.resources.report],
              accessTo: ["all"],
              featureKey: FeatureKeys.REPORTING_ANALYTICS,
            },
          ],
        },
      ],
    },
  ];
};

export const hasAccessToModule = (moduleAllowedResources: string[] = []) => {
  const userResources = store.getState().auth.resources;
  const hasAccess = moduleAllowedResources.every(
    (individualResource: any) =>
      userResources.hasOwnProperty(individualResource) &&
      userResources[individualResource]?.includes(strings.fetchPermission),
  );
  return hasAccess;
};

export const useHasAccess = (path: string) => {
  return true;
  // const sections = useSections();
  // const section = sections[0].items.find((sec) => path.includes(sec.path));
  // if (section) {
  //   return permissionCheck(section);
  // }
  // return false;
};

export const hasAccessTo = (resource: string, permission: string) => {
  const resources = store.getState().auth.resources;
  if (resources.hasOwnProperty(strings.resources.app)) {
    return true;
  }
  if (
    resources.hasOwnProperty(resource) &&
    resources[resource].includes(strings.adminPermission)
  ) {
    return true;
  }
  if (!resources.hasOwnProperty(resource)) {
    return false;
  }
  return resources[resource].includes(permission);
};

/**
 * Gate a workspace-scoped action (document.send / void / template.manage …) by the caller's role in
 * the ACTIVE workspace. In personal mode (no active workspace) it is not gated — account-level checks
 * still apply. Admins bypass. Mirrors the backend @RequiresWorkspacePermission so the UI hides what
 * would otherwise return a 403.
 */
export const hasWorkspacePermission = (permission: string): boolean => {
  const wp = store.getState().auth.workspacePermissions;
  if (!wp || !wp.scoped) return true;
  if (wp.admin) return true;
  return Array.isArray(wp.permissions) && wp.permissions.includes(permission);
};

export const useMakeUserAllowedSections = () => {
  const sections = useSections();

  const filterItems = (items: Item[]): Item[] => {
    return items
      .filter((item) => item.visibleInSideNav !== false)
      .filter((item) => permissionCheck(item))
      .map((item) => {
        if (item.items) {
          return {
            ...item,
            items: filterItems(item.items),
          };
        }
        return item;
      })
      .filter((item) => !item.items || item.items.length > 0 || !!item.path);
  };

  return sections
    .map((section) => {
      const filteredItems = filterItems(section.items);

      return {
        ...section,
        items: filteredItems,
      };
    })
    .filter((section) => section.items.length > 0);
};

export const permissionCheck = (item: Item) => {
  if (item.accessTo.includes("all")) {
    // Permission will be checked in internal pages
    return true;
  }
  if (
    item.accessTo.includes("external") &&
    item.accessTo.includes("internal")
  ) {
    if (isAdmin()) {
      return true;
    }
  }
  if (
    item.accessTo.includes("external") &&
    item.accessTo.includes("internal")
  ) {
    return hasAccessToModule(item.accessToResource);
  }
  if (item.accessTo.includes("external")) {
    return isExternalUser() && hasAccessToModule(item.accessToResource);
  }
  if (isAdmin()) {
    return true;
  }
  if (item.accessTo.includes("internal")) {
    return !isExternalUser() && hasAccessToModule(item.accessToResource);
  }
  return false;
};

// export const useAuthRouteRedirection = () => {
//   const routes = useMakeUserAllowedSections();
//   if (routes[0].items.length > 0) {
//     return routes[0].items[0].path;
//   }
//   return viewpaths.pageNotFoundViewPath;
// };

export const useAuthRouteRedirection = () => {
  const routes = useMakeUserAllowedSections();

  if (routes.length > 0 && routes[0].items.length > 0) {
    const firstItem: Item = routes[0].items[0];

    const redirectPath =
      firstItem.path || (firstItem.items && firstItem.items[0].path);

    if (redirectPath) {
      return redirectPath;
    }
  }

  return viewpaths.pageNotFoundViewPath;
};

export const hasAccessToAccount = () => {
  const resources = store.getState().auth.resources;
  if (
    resources.hasOwnProperty(strings.resources.account) &&
    resources.hasOwnProperty(strings.resources.app)
  ) {
    return true;
  }
  return false;
};

// Access Checks

export const isExternalUser = () => {
  const resources = store.getState().auth.resources;
  return Object.keys(resources).some((resource: string) =>
    resources[resource].includes(strings.selfPermission),
  );
};

export const isAdmin = () => {
  const resources = store.getState().auth.resources;
  if (
    resources.hasOwnProperty(strings.resources.app) &&
    resources[strings.resources.app].includes(strings.adminPermission)
  ) {
    return true;
  }
  return false;
};

export const isDarkTheme = () => {
  const theme = store.getState().preference.theme;
  if (theme.includes("dark")) {
    return true;
  }
  return false;
};
